/**
 * @file App shell for parent-item detail views. Domain detail adapters own
 * item-specific fields, section decisions, metadata, and post-render hooks.
 */

// --- Imports ---

// State management
import { state, appData, appDataDetails } from '../state/state.js';
import {
    getParentDetailAdapter,
    getParentDetailStatusOptions,
    getParentDetailTypeOptions,
    renderParentDetailSections,
} from './parentDetailAdapterRegistry.js';
import {
    captureDetailFormDraft,
    captureDetailUiDraft,
    clearDetailDirtyField,
    clearDetailDirtyFields,
    enableDetailSaveButtons,
    hasDetailDirtyFields,
    isDetailFieldDirty,
    recordDetailFieldBaseline,
    recordDetailFormBaseline,
    restoreDetailFormDraft,
    syncDetailDirtyScope,
} from '../../shared/details/detailDirtyState.js';
import {
    attachAttachmentsSectionListeners,
    renderAttachmentsSection,
} from '../../shared/details/attachmentsSection.js';
import {
    attachChecklistListeners,
    renderChecklistSection,
} from '../../shared/details/checklistSection.js';
import {
    attachLinkedTaskSectionListeners,
    renderTaskSection,
} from '../../shared/details/linkedItemsSection.js';
import {
    attachNotesSectionListeners,
    renderNotesSection,
} from '../../shared/details/notesSection.js';
import {
    bindLongTextDisplay,
    getLongTextContainer,
    getLongTextFieldName,
    getLongTextValueField,
    setLongTextContainerValue,
    syncLongTextDisplaysFromForm,
    updateLongTextOverflow,
} from '../../shared/details/detailLongText.js';
import {
    createDetailLiveSaveQueue,
} from '../../shared/details/detailLiveSaveQueue.js';
import {
    openDetailLeaveModal,
    savePendingDetailDrafts,
} from '../../shared/details/detailLeaveGuard.js';
import { renderDetailShell } from '../../shared/details/detailShell.js';
import {
    destroyRegisteredControlsIn,
    focusFirstRegisteredControlError,
    updateControlValue,
    validateRegisteredControl,
    validateRegisteredControls,
} from '../../shared/ui/controls/controlCore.js';
// Utility functions
import {
    findItem,
    getItemType,
} from "../../shared/utils/itemLookup.js";
import {
    formatCurrency,
    isColorDark,
} from "../../shared/utils/displayUtils.js";
import {
    formatAndSanitizeUserContent,
    sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import {
    getMatchingChecklistTemplates,
} from "../../shared/details/checklistTemplates.js";
// Navigation
import { navigate, showDetails } from '../routing/navigationController.js';
// API functions (for attaching event listeners)
import {
    handleDetailFieldChange,
    saveDetailTextField,
    saveDetailsForm,
} from '../../shared/api/details.js';
import {
    handleAddNote,
    handleSaveComment,
    saveNoteContent,
} from '../../shared/api/notes.js';
import {
    handleAttachmentBulkDelete,
    handleAttachmentBulkDownload,
    handleAttachmentLinkCopy,
    handleAttachmentMetadataSave,
    handleAttachmentOfficePreview,
    handleAttachmentVersionUpload,
    handleFileDelete,
    handleFileUpload,
} from '../../shared/api/attachments.js';
import {
    quickCloseTask,
    saveChecklistState,
    toggleChecklistItem,
} from '../../shared/api/checklists.js';
import { handleDeleteItem } from '../../shared/api/parentItems.js';
// Modal/Form functions (for attaching event listeners and generating parts)
import { openParentForm } from '../../shared/forms/parentFormModal.js';
import { openDynamicFieldsModal } from '../../shared/forms/dynamicFieldsModal.js';
import {
    bindNativeFieldBehavior,
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
    validateNativeFormFields,
} from '../../shared/forms/nativeFieldStyles.js';
import { applyBrandingVariables } from '../../shared/ui/brandingTokens.js';
import { openConfirmationModal } from '../../shared/ui/confirmationModal.js';

export { updateAuditTrailUI } from '../../shared/details/detailSections.js';

const detailLiveSaveQueue = createDetailLiveSaveQueue({
    handleDetailFieldChange,
    onSaveFailure(save) {
        if (save?.itemId !== state.selectedItemId) {return;}
        renderDetailsView();
        updateSelectBackgrounds();
    },
});

function resetDetailSaveButtons(detailsViewContainer) {
    detailsViewContainer.querySelectorAll('.details-save-btn').forEach(btn => {
        btn.textContent = 'Save';
        btn.classList.remove(
            'bg-[var(--dfc-brand-primary)]',
            'hover:bg-[var(--dfc-brand-primary-hover)]',
            'shadow-md',
            'text-gray-900',
        );
        btn.classList.add('bg-gray-200', 'hover:bg-gray-300', 'text-gray-800');
        btn.disabled = true;
    });
}

function setDetailSaveButtonsSaving(detailsViewContainer) {
    detailsViewContainer.querySelectorAll('.details-save-btn').forEach(btn => {
        btn.textContent = 'Saving...';
        btn.disabled = true;
        btn.classList.remove('hover:bg-[var(--dfc-brand-primary-hover)]');
    });
}

function refreshDetailSaveStateAfterDetailsClear(detailsViewContainer, detailsForm) {
    if (hasDetailDirtyFields(detailsForm)) {
        enableDetailSaveButtons(detailsViewContainer);
        return;
    }

    state.hasUnsavedChanges = false;
    resetDetailSaveButtons(detailsViewContainer);
}

// --- Main Detail View Renderer ---

/**
 * Renders the entire content of the details view (#details-view) based on the
 * item currently selected in `state.selectedItemId`. Attaches necessary event listeners
 * for interactions within the details view.
 */
export function renderDetailsView() {
    const detailsView = document.getElementById('details-view');
    if (!detailsView) { console.error("Details view container (#details-view) not found."); return; }
    applyBrandingVariables(detailsView, appData);
    syncDetailDirtyScope();
    const dirtyUiDraft = captureDetailUiDraft(detailsView);
    const dirtyDraft = captureDetailFormDraft(detailsView.querySelector('#details-form'));
    const keepDirtyState = state.hasUnsavedChanges || Boolean(dirtyDraft?.length);
    destroyRegisteredControlsIn(detailsView);

    let item = findItem(state.selectedItemId); // from utils.js
    let itemType = getItemType(item?.id); // from utils.js

    // If the item wasn't found in global state, check the detail task list.
    if (!item && state.selectedItemId) {
        item = appDataDetails.tasks.find(t => t.id === state.selectedItemId);

        // If we found it here, we KNOW its type is 'task'.
        if (item) {
            itemType = 'task';
        }
    }

    if (!item) {
        detailsView.innerHTML = '<p class="p-8 text-center text-gray-500">Item not found or could not be loaded.</p>';
        return;
    }

    if (!itemType) {
        detailsView.innerHTML = `<p class="p-8 text-center text-red-500">Error: Could not determine type for item ID ${sanitizeHTML(item.id)}.</p>`;
        return;
    }

    const optimisticDetailValues = detailLiveSaveQueue.getOptimisticDetailValues(item.id);
    if (Object.keys(optimisticDetailValues).length > 0) {
        item = {
            ...item,
            ...optimisticDetailValues,
        };
    }

    const detailAdapter = getParentDetailAdapter(itemType);
    if (!detailAdapter) {
        detailsView.innerHTML = `<p class="p-8 text-center text-red-500">Error: No detail adapter registered for ${sanitizeHTML(itemType)}.</p>`;
        return;
    }

    const detailsFieldsHtml = detailAdapter.renderFields(item);
    const sectionsHtml = renderParentDetailSections(detailAdapter, item, {
        attachments: renderAttachmentsSection,
        checklist: renderChecklistSection,
        linkedTasks: (sectionItem) => renderTaskSection(sectionItem.id),
        notes: renderNotesSection,
    });

    detailsView.innerHTML = renderDetailShell({
        itemType,
        fieldsHtml: detailsFieldsHtml,
        sectionsHtml,
    });

    bindNativeFieldBehavior(detailsView.querySelector('#details-form'));
    detailAdapter.mountControls?.(detailsView, item);

    if (!keepDirtyState) {
        clearDetailDirtyFields();
    }
    // Compare drafts with stored values, rather than treating restored edits as saved.
    recordDetailFormBaseline(detailsView.querySelector('#details-form'));

    // --- Attach Event Listeners ---
    if (dirtyDraft) {
        restoreDetailFormDraft(detailsView.querySelector('#details-form'), dirtyDraft);
        syncLongTextDisplaysFromForm(detailsView);
    }
    attachDetailViewListenersLocal(
        detailsView,
        item,
        itemType,
        detailAdapter,
        dirtyUiDraft,
    ); // Attach listeners specific to this rendered content
    if (keepDirtyState) {
        enableDetailSaveButtons(detailsView);
    } else {
        state.hasUnsavedChanges = false;
    }
    updateSelectBackgrounds(); // Update colors after rendering
}

/**
 * Updates the background color and text color of priority and status select elements
 * based on the selected option's configuration. Used in the details view form.
 */
export function updateSelectBackgrounds() {
    const priorities = appData?.config?.priorities || [];

    // Update Priority Select
    const prioritySelect = document.querySelector('#details-form select[name="priority"]');
    if (prioritySelect) {
        const selectedPriority = priorities.find(p => p.id === prioritySelect.value);
        if (selectedPriority?.color) {
            prioritySelect.style.backgroundColor = selectedPriority.color;
            prioritySelect.style.color = isColorDark(selectedPriority.color) ? '#ffffff' : '#374151'; // Use dark gray text
        } else {
            prioritySelect.style.backgroundColor = ''; // Reset if no color
            prioritySelect.style.color = '';
        }
    }

    // Update Status Selects (covers 'status' and 'equipment_status')
    const statusSelects = document.querySelectorAll('#details-form select[name="status"], #details-form select[name="equipment_status"]');
    statusSelects.forEach(statusSelect => {
        const itemType = getItemType(state.selectedItemId); // from utils.js
        const statusList = getParentDetailStatusOptions(
            getParentDetailAdapter(itemType),
            appData,
        );
        const selectedStatus = statusList.find(s => s.id === statusSelect.value);
        if (selectedStatus?.color) {
            statusSelect.style.backgroundColor = selectedStatus.color;
            statusSelect.style.color = isColorDark(selectedStatus.color) ? '#ffffff' : '#374151';
        } else {
            statusSelect.style.backgroundColor = '';
            statusSelect.style.color = '';
        }
    });

    // Update Date Placeholder Colors
    const dateInputs = document.querySelectorAll('#details-form input[type="date"], #details-form input[type="datetime-local"]');
    dateInputs.forEach(input => {
        if (!input.value) {
            input.classList.add('text-gray-500', 'dark:text-gray-400');
            input.classList.remove('text-gray-900', 'dark:text-gray-200');
        } else {
            input.classList.remove('text-gray-500', 'dark:text-gray-400');
            input.classList.add('text-gray-900', 'dark:text-gray-200');
        }

        // Ensure listener is attached if not already
        if (!input.dataset.colorListener) {
            input.addEventListener('change', () => updateSelectBackgrounds());
            input.addEventListener('input', () => updateSelectBackgrounds());
            input.dataset.colorListener = 'true';
        }
    });
}

function getDetailTypeOptions(itemType) {
    return getParentDetailTypeOptions(getParentDetailAdapter(itemType), appData);
}

function getAddedTypeIds(previousTypes, nextTypes) {
    const previousSet = new Set(previousTypes.map((typeId) => String(typeId)));
    return nextTypes.filter((typeId) => !previousSet.has(String(typeId)));
}

async function promptForChecklistTemplates(item, itemType, newTypeId) {
    const matchingTemplates = getMatchingChecklistTemplates(itemType, [newTypeId], false);
    if (matchingTemplates.length === 0) {return;}

    let messageHtml;
    let heading;
    if (matchingTemplates.length > 1) {
        heading = 'Checklist Templates Available';
        const checkboxesHtml = matchingTemplates.map((template) => `
            <label class="${NATIVE_CHECKBOX_LABEL_CLASSES} mb-2 items-start" data-native-checkbox-label>
                <input type="checkbox" name="auto-apply-template" value="${sanitizeHTML(template.id)}" class="${NATIVE_CHECKBOX_CLASSES}" checked>
                <span class="${NATIVE_CHECKBOX_MARKER_CLASSES} mt-0.5" data-native-checkbox-marker>
                    <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
                </span>
                <span class="text-sm text-gray-700 dark:text-gray-200">${sanitizeHTML(template.name)} checklist template</span>
            </label>
        `).join('');
        messageHtml = `
            <div class="mb-4 text-sm text-gray-600 dark:text-gray-400">Several checklist templates are available for this item type. Select any that you would like to apply.</div>
            <div id="auto-checklist-template-checkboxes" class="mt-2 text-left">
                ${checkboxesHtml}
            </div>
        `;
    } else {
        heading = 'Checklist Template Available';
        messageHtml = `<strong>${sanitizeHTML(matchingTemplates[0].name)}</strong> checklist template is available for this item type. Would you like to add it?`;
    }

    openConfirmationModal(heading, messageHtml, async () => {
        let selectedTemplates;
        if (matchingTemplates.length > 1) {
            const checkboxes = document.querySelectorAll(
                '#auto-checklist-template-checkboxes input[name="auto-apply-template"]:checked',
            );
            const selectedIds = Array.from(checkboxes).map(cb => cb.value);
            selectedTemplates = matchingTemplates.filter(template =>
                selectedIds.includes(template.id),
            );
        } else {
            selectedTemplates = [matchingTemplates[0]];
        }

        if (selectedTemplates.length === 0) {return;}

        const currentItem = findItem(item.id) || item;
        const newData = { ...(currentItem.checklist || {}) };
        let orderIndex = Object.keys(newData).length;

        selectedTemplates.forEach(template => {
            if (!template || !template.items) {return;}
            const itemsArray = Object.values(template.items).sort((a, b) =>
                (a.order || 0) - (b.order || 0),
            );
            itemsArray.forEach(checklistItem => {
                newData[crypto.randomUUID()] = {
                    text: checklistItem.text,
                    isCompleted: false,
                    order: orderIndex++,
                };
            });
        });

        currentItem.checklist = newData;
        item.checklist = newData;

        await saveChecklistState(item.id, itemType, newData);
        renderDetailsView();
    }, false, 'Apply', null, 'Applying...', "Don't Apply");
}

function appendDynamicTypeMarkdown(detailsViewContainer, item, itemType, markdownText) {
    if (!markdownText) {return;}

    const textFieldName = getLongTextFieldName(itemType);
    const textarea = detailsViewContainer.querySelector(
        `.long-text-field-container[data-field-name="${textFieldName}"] .long-text-editor`,
    );
    const valueField = detailsViewContainer.querySelector(
        `.long-text-field-container[data-field-name="${textFieldName}"] [data-long-text-value]`,
    );

    if (textarea) {
        const currentVal = textarea.value || '';
        textarea.value = currentVal ?
            `${currentVal}\n${markdownText}` :
            markdownText.trim();
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
        textarea.scrollTop = textarea.scrollHeight;
        return;
    }

    const currentVal = item[textFieldName] || '';
    const draftVal = valueField?.value || '';
    if (valueField && draftVal !== currentVal) {
        const nextDraft = draftVal ?
            `${draftVal}\n${markdownText}` :
            markdownText.trim();
        valueField.value = nextDraft;

        const displayContainer = detailsViewContainer.querySelector(
            `.long-text-field-container[data-field-name="${textFieldName}"] .long-text-display`,
        );
        if (displayContainer) {
            displayContainer.innerHTML = formatAndSanitizeUserContent(nextDraft);
            requestAnimationFrame(() => {
                const container = displayContainer.closest('.long-text-field-container');
                if (container) {updateLongTextOverflow(container);}
            });
        }
        enableDetailSaveButtons(detailsViewContainer, textFieldName);
        return;
    }

    const newVal = currentVal ?
        `${currentVal}\n${markdownText}` :
        markdownText.trim();

    void handleSaveComment(newVal, item.id);

    const displayContainer = detailsViewContainer.querySelector(
        `.long-text-field-container[data-field-name="${textFieldName}"] .long-text-display`,
    );
    if (valueField) {
        valueField.value = newVal;
        recordDetailFieldBaseline(
            detailsViewContainer.querySelector('#details-form'),
            textFieldName,
        );
    }
    if (displayContainer) {
        displayContainer.innerHTML = formatAndSanitizeUserContent(newVal);
        requestAnimationFrame(() => {
            const container = displayContainer.closest('.long-text-field-container');
            if (container) {updateLongTextOverflow(container);}
        });
    }

    item[textFieldName] = newVal;
}

function persistDetailTypeChange(commitTypes) {
    void commitTypes().catch((error) => {
        console.error('Error saving detail type change:', error);
    });
}

function continueAfterDetailsDraftResolution({
    continueFlow,
    controlId,
    previousTypes,
}) {
    if (controlId) {
        updateControlValue(controlId, previousTypes, { silent: true });
    }
    continueFlow();
}

function guardDynamicFieldsForDetailsDraft({
    detailsViewContainer,
    item,
    itemType,
    controlId,
    previousTypes,
    continueFlow,
}) {
    const detailsForm = detailsViewContainer.querySelector('#details-form');
    const textFieldName = getLongTextFieldName(itemType);
    const container = getLongTextContainer(detailsViewContainer, textFieldName);
    const valueField = container ? getLongTextValueField(container) : null;
    const savedValue = item[textFieldName] || '';
    const draftValue = valueField?.value || '';
    const hasUnsavedDetailsDraft = draftValue !== savedValue ||
        isDetailFieldDirty(detailsForm, textFieldName);

    if (!hasUnsavedDetailsDraft) {
        continueAfterDetailsDraftResolution({
            continueFlow,
            controlId,
            previousTypes,
        });
        return;
    }

    const enableLongTextSave = (fieldNames) => {
        enableDetailSaveButtons(detailsViewContainer, fieldNames);
    };

    updateControlValue(controlId, previousTypes, { silent: true });
    openConfirmationModal(
        'Unsaved Details',
        'You have unsaved changes in the details field. What would you like to do with the unsaved changes?',
        async () => {
            const didSave = await saveDetailTextField(textFieldName, draftValue);
            if (!didSave) {return;}

            item[textFieldName] = draftValue;
            setLongTextContainerValue(container, draftValue, enableLongTextSave);
            recordDetailFieldBaseline(detailsForm, textFieldName);
            clearDetailDirtyField(textFieldName);
            refreshDetailSaveStateAfterDetailsClear(detailsViewContainer, detailsForm);
            continueFlow();
        },
        false,
        'Save',
        null,
        'Saving...',
        'Cancel',
        {
            closeOnError: false,
            leftAction: {
                buttonText: 'Discard',
                danger: true,
                onClick: async () => {
                    setLongTextContainerValue(container, savedValue, enableLongTextSave);
                    recordDetailFieldBaseline(detailsForm, textFieldName);
                    clearDetailDirtyField(textFieldName);
                    refreshDetailSaveStateAfterDetailsClear(detailsViewContainer, detailsForm);
                    continueFlow();
                },
            },
        },
    );
}

async function handleDetailTypesChange(event, detailsViewContainer, item, itemType) {
    const detailAdapter = getParentDetailAdapter(itemType);
    const controlId = event.detail?.id || '';
    const previousTypes = Array.isArray(item?.types) ?
        item.types.map((typeId) => String(typeId)) :
        [];
    const nextTypes = Array.isArray(event.detail?.value) ?
        event.detail.value.map((typeId) => String(typeId)).filter(Boolean) :
        [];
    const addedTypes = getAddedTypeIds(previousTypes, nextTypes);
    const addedTypeId = addedTypes.length === 1 ? addedTypes[0] : '';
    const typeConfig = addedTypeId ?
        getDetailTypeOptions(itemType).find(type => type.id === addedTypeId) :
        null;

    const commitTypes = async () => {
        let saveResult;
        if (typeof detailAdapter?.saveRegisteredDetailValue === 'function') {
            saveResult = await detailAdapter.saveRegisteredDetailValue({
                controlId,
                detailsForm: detailsViewContainer.querySelector('#details-form'),
                fieldName: 'types',
                item,
                itemType,
                renderDetailsView,
                updateSelectBackgrounds,
                value: nextTypes,
            });
        } else {
            saveResult = await detailLiveSaveQueue.saveRegisteredDetailValue({
                item,
                itemType,
                fieldName: 'types',
                value: nextTypes,
            });
        }
        if (saveResult === false) {return false;}
        item.types = nextTypes;
        if (detailAdapter?.validateEmptyTypesOnClear && controlId && nextTypes.length === 0) {
            validateRegisteredControl(controlId);
        }
        return true;
    };

    if (typeConfig?.fields?.length > 0 && controlId) {
        const openTypeDynamicFieldsModal = () => openDynamicFieldsModal(
            typeConfig,
            itemType,
            async (markdownText) => {
                updateControlValue(controlId, nextTypes, { silent: true });
                item.types = nextTypes;
                persistDetailTypeChange(commitTypes);
                appendDynamicTypeMarkdown(
                    detailsViewContainer,
                    item,
                    itemType,
                    markdownText,
                );
                promptForChecklistTemplates(item, itemType, addedTypeId);
            },
            () => {
                updateControlValue(controlId, previousTypes, { silent: true });
            },
            () => {
                updateControlValue(controlId, previousTypes, { silent: true });
            },
        );
        guardDynamicFieldsForDetailsDraft({
            detailsViewContainer,
            item,
            itemType,
            controlId,
            previousTypes,
            continueFlow: openTypeDynamicFieldsModal,
        });
        return;
    }

    persistDetailTypeChange(commitTypes);
    item.types = nextTypes;
    if (addedTypeId) {
        promptForChecklistTemplates(item, itemType, addedTypeId);
    }
}


// --- Internal Event Listener Setup for Details View ---

/**
 * Attaches event listeners specific to the elements within the details view. (Internal helper)
 * @param {HTMLElement} detailsViewContainer - The main container element (#details-view).
 * @param {object} item - The currently viewed item.
 * @param {string} itemType - The type of the currently viewed item.
 * @param {object} detailAdapter - Domain detail adapter for the item type.
 * @param {object} uiDraft - Transient detail UI state to restore after re-render.
 */
function attachDetailViewListenersLocal(
    detailsViewContainer,
    item,
    itemType,
    detailAdapter,
    uiDraft = {},
) {
    if (!detailsViewContainer || !item || !itemType) {return;}
    const hasSection = (sectionId) => (detailAdapter.sections || []).includes(sectionId);

    // --- 1. Navigation Helper with "Unsaved Changes" Protection ---
    const performNavigationTarget = (target, fallbackView) => {
        if (target?.type === 'detail' && target.id) {
            showDetails(target.id, target.itemType || null);
            return;
        }
        navigate(target?.view || fallbackView);
    };

    const enableSaveButtons = (fieldNames = []) => {
        detailsViewContainer.querySelectorAll('.details-save-btn').forEach((btn) => {
            btn.textContent = 'Save';
        });
        enableDetailSaveButtons(detailsViewContainer, fieldNames);
    };

    const saveCurrentDetailsForm = async (detailsForm) => detailAdapter.saveManualDetails ?
        detailAdapter.saveManualDetails({
            detailsForm,
            item,
            itemType,
        }) :
        saveDetailsForm(detailsForm);

    const onSavePendingDetailDrafts = async () => {
        const didSave = await savePendingDetailDrafts({
            root: detailsViewContainer,
            saveDetailsForm: saveCurrentDetailsForm,
            saveNoteContent,
        });
        if (didSave) {
            return true;
        }
        return false;
    };

    const navigateBack = () => {
        const performNavigation = () => {
            const previousDetail = state.detailNavigationStack.pop();
            if (previousDetail?.id) {
                showDetails(previousDetail.id, previousDetail.type || null, {
                    fromBack: true,
                });
                return;
            }

            if (state.originatingView === 'settings' || state.settingsReturnContext?.tab) {
                navigate('settings');
                return;
            }
            performNavigationTarget(
                detailAdapter.getBackNavigation?.({
                    findItem,
                    item,
                    itemType,
                    state,
                }),
                detailAdapter.viewName || `${itemType}s`,
            );
        };

        const modalOpened = openDetailLeaveModal({
            onContinue: performNavigation,
            onSave: async () => {
                const didSave = await onSavePendingDetailDrafts();
                if (didSave) {
                    performNavigation();
                }
            },
        });

        if (!modalOpened) {
            performNavigation();
        }
    };

    // --- 2. Back Button ---
    detailsViewContainer.querySelectorAll('#details-back-btn, #details-back-btn-mobile').forEach((backBtn) => {
        backBtn.onclick = navigateBack;
    });

    const parentLinkBtn = detailsViewContainer.querySelector('[data-parent-link]');
    if (parentLinkBtn) {
        parentLinkBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            showDetails(parentLinkBtn.dataset.parentLink);
        };
    }

    // --- 3. Save Buttons (Manual Save) ---
    detailsViewContainer.querySelectorAll('.details-save-btn').forEach(btn => {
        btn.onclick = async () => {
            const detailsForm = detailsViewContainer.querySelector('#details-form');
            if (detailsForm) {
                // Validation check
                const nativeValid = validateNativeFormFields(detailsForm);
                const registeredValidation = validateRegisteredControls(detailsForm);
                if (!nativeValid || !registeredValidation.valid) {
                    if (nativeValid) {
                        focusFirstRegisteredControlError(registeredValidation);
                    }
                    return;
                }

                if (detailAdapter.showManualSavePendingState === true) {
                    setDetailSaveButtonsSaving(detailsViewContainer);
                } else {
                    resetDetailSaveButtons(detailsViewContainer);
                }

                try {
                    const shouldContinueSave = await detailAdapter.prepareManualSave?.({
                        detailsForm,
                        item,
                        itemType,
                    });
                    if (shouldContinueSave === false) {
                        enableSaveButtons();
                        return;
                    }

                    const didSave = await saveCurrentDetailsForm(detailsForm);
                    if (!didSave) {
                        enableSaveButtons();
                        return;
                    }
                    // state.hasUnsavedChanges is set to false inside saveDetailsForm on success
                    clearDetailDirtyFields();
                    resetDetailSaveButtons(detailsViewContainer);
                } catch (err) {
                    console.error("Save failed", err);
                    enableSaveButtons();
                }
            } else {
                navigateBack();
            }
        };
    });

    // --- 4. Delete Button (Bottom) ---
    detailsViewContainer.querySelectorAll('#delete-item-btn-bottom, #delete-item-btn-mobile').forEach((deleteBtn) => {
        deleteBtn.onclick = () => {
            openConfirmationModal(
                `Delete ${itemType}?`,
                `
                    <p>Do you really want to delete this ${sanitizeHTML(itemType)}? Deleting it will also delete all linked subtasks, attachments and other linked items.</p>
                    <p class="mt-3">This action cannot be undone.</p>
                `,
                async () => {
                    const deleteResult = await handleDeleteItem(item.id, itemType);
                    if (deleteResult === false || deleteResult?.success === false) {
                        return false;
                    }
                    // Force navigation (delete implies we leave)
                    state.hasUnsavedChanges = false;
                    clearDetailDirtyFields();
                    performNavigationTarget(
                        detailAdapter.getDeleteNavigation?.({
                            findItem,
                            item,
                            itemType,
                            state,
                        }),
                        detailAdapter.viewName || `${itemType}s`,
                    );
                }
            );
        };
    });

    // --- 5. Dirty Flag Listener (Replaces Live Save) ---
    const detailsForm = detailsViewContainer.querySelector('#details-form');
    if (detailsForm) {
        const liveSaveFields = new Set(detailAdapter.liveSaveFields || []);
        const markDirtyFromEvent = (e) => {
            if (e.target.dataset?.controlMirror) {return;}
            if (e.target.name && !e.target.classList.contains('no-live-save') && !liveSaveFields.has(e.target.name)) {
                enableSaveButtons(e.target.name);
            }
        };
        detailsForm.oninput = markDirtyFromEvent;
        detailsForm.onchange = markDirtyFromEvent;

        detailsForm.addEventListener('control:valuechange', async (event) => {
            const fieldName = event.detail?.name || '';
            const value = event.detail?.value;
            if (fieldName.startsWith('recurrence_')) {
                return;
            }
            if (fieldName === 'types') {
                await handleDetailTypesChange(
                    event,
                    detailsViewContainer,
                    item,
                    itemType,
                );
                return;
            }
            if (detailAdapter.handleControlValueChange?.({
                event,
                item,
                itemType,
                renderDetailsView,
                updateSelectBackgrounds,
                value,
                enableSaveButtons,
                saveRegisteredDetailValue: detailLiveSaveQueue.saveRegisteredDetailValue,
            })) {
                return;
            }

            if (!liveSaveFields.has(fieldName)) {
                if (fieldName) {
                    enableSaveButtons(fieldName);
                }
                return;
            }

            updateSelectBackgrounds();
            await detailLiveSaveQueue.saveRegisteredDetailValue({
                item,
                itemType,
                fieldName,
                value: value ?? '',
            });
        });
    }

    detailAdapter.attachPostFormListeners?.(detailsViewContainer, item, {
        currentUserId: state.currentUser?.uid,
        enableSaveButtons,
        handleDeleteItem,
        openConfirmationModal,
        renderDetailsView,
        showDetails,
        uiDraft,
    });

    detailsViewContainer.onchange = (e) => {
        if (e.target.tagName === 'SELECT' && !e.target.classList.contains('add-multi-select')) {
            if (['status', 'priority', 'equipment_status'].includes(e.target.name)) {
                updateSelectBackgrounds();
                handleDetailFieldChange(e.target);
            } else {
                enableSaveButtons(e.target.name);
            }
        }
    };

    if (hasSection('attachments')) {
        attachAttachmentsSectionListeners(detailsViewContainer, item, {
            handleAttachmentBulkDelete,
            handleAttachmentBulkDownload,
            handleAttachmentLinkCopy,
            handleAttachmentMetadataSave,
            handleAttachmentOfficePreview,
            handleAttachmentVersionUpload,
            handleFileDelete,
            handleFileUpload,
        });
    }
    if (hasSection('notes')) {
        attachNotesSectionListeners(detailsViewContainer, {
            handleAddNote,
        });
    }
    if (hasSection('linkedTasks')) {
        attachLinkedTaskSectionListeners(detailsViewContainer, item.id, {
            renderDetailsView,
        });
    }

    // --- 9. Long Text Field Editing (Description/Comments) ---
    detailsViewContainer.querySelectorAll('.long-text-field-container').forEach(container => {
        bindLongTextDisplay(container, enableSaveButtons);
        requestAnimationFrame(() => updateLongTextOverflow(container));
        window.addEventListener('resize', () => updateLongTextOverflow(container));
    });



    // --- 11. Task List Item Clicks ---
    detailsViewContainer.querySelectorAll('.task-list-item').forEach(taskItem => {
        taskItem.onclick = (e) => {
            if (e.target.closest('button')) {
                return;
            }
            e.stopPropagation();
            showDetails(taskItem.dataset.id);
        };
    });

    detailsViewContainer.querySelectorAll('.task-quick-close-btn').forEach((button) => {
        button.onclick = async (e) => {
            e.stopPropagation();
            button.disabled = true;
            try {
                await quickCloseTask(button.dataset.taskId);
            } catch (error) {
                console.error('Failed to quick close task:', error);
            } finally {
                button.disabled = false;
            }
        };
    });

    // --- 12. Add Task Buttons ---
    detailsViewContainer.querySelectorAll('.quick-add-task-btn').forEach(btn => {
        btn.onclick = (e) => {
            e.stopPropagation();
            const parentId = btn.dataset.assetId || btn.dataset.patientId || btn.dataset.projectId || item.id;
            const formType = btn.dataset.formType;
            if (parentId && formType) {
                openParentForm(formType, { parentId: parentId, owner: state.currentUser?.uid });
            }
        };
    });

    // --- 14. Currency Input Formatting ---
    detailsViewContainer.querySelectorAll('.currency-input').forEach(input => {
        const parseCurrency = () => {
            const rawValue = input.value.trim();
            if (!rawValue) {return null;}
            if (/[^0-9,$.\-\s]/.test(rawValue)) {return null;}
            const cleanedValue = rawValue.replace(/[^0-9.-]/g, '');
            if (!/\d/.test(cleanedValue)) {return null;}
            const numericValue = Number(cleanedValue);
            return Number.isFinite(numericValue) ? numericValue : null;
        };
        const validateCurrency = () => {
            const rawValue = input.value.trim();
            const isValid = !rawValue || parseCurrency() !== null;
            input.setCustomValidity(isValid ? '' : 'Enter a valid amount.');
            return isValid;
        };

        validateCurrency();
        input.addEventListener('focus', (e) => {
            // Remove $ and commas to show raw number for editing
            const numericValue = parseCurrency();
            e.target.value = numericValue === null ? e.target.value : String(numericValue);
            if (e.target.value) {e.target.select();}
            validateCurrency();
        });
        input.addEventListener('input', validateCurrency);
        input.addEventListener('blur', (e) => {
            // Format the *current input value* immediately
            const numericValue = parseCurrency();
            if (numericValue !== null) {
                e.target.value = formatCurrency(numericValue);
            } else if (!e.target.value.trim()) {
                e.target.value = '';
            }
            validateCurrency();
        });
    });

    if (hasSection('checklist')) {
        attachChecklistListeners(detailsViewContainer, item, itemType, {
            renderDetailsView,
            saveChecklistState,
            toggleChecklistItem,
        });
    }
}
